import { ChickenEmpireGame } from './game';
import { COUNTRIES, FUN_FACTS } from './data';
import type { GameState, RandomEvent, CountryCode } from './types';

const app = document.getElementById('app')!;
let game: ChickenEmpireGame;

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function showToast(message: string, type: 'success' | 'info' | 'warning' = 'info') {
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.innerHTML = message;
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('show');
  });

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 4000);
}

function renderTitleScreen() {
  app.innerHTML = `
    <style>
      .title-screen {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        text-align: center;
      }
      .title-chicken {
        font-size: 8rem;
        animation: bounce 2s ease-in-out infinite;
        filter: drop-shadow(0 10px 30px rgba(233, 69, 96, 0.3));
      }
      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-20px); }
      }
      .title-text {
        font-family: 'Lilita One', cursive;
        font-size: 4rem;
        background: linear-gradient(135deg, #ffc947 0%, #e94560 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin: 1rem 0;
        text-shadow: 0 4px 20px rgba(255, 201, 71, 0.3);
      }
      .title-subtitle {
        font-size: 1.5rem;
        color: #888;
        margin-bottom: 2rem;
      }
      .title-fact {
        max-width: 500px;
        padding: 1rem 1.5rem;
        background: rgba(255,255,255,0.05);
        border-radius: 12px;
        margin-bottom: 2rem;
        font-size: 1rem;
        color: #aaa;
        border-left: 3px solid var(--gold);
      }
      .start-btn {
        font-family: 'Lilita One', cursive;
        font-size: 1.5rem;
        padding: 1rem 3rem;
        background: linear-gradient(135deg, #e94560 0%, #c73659 100%);
        color: white;
        border: none;
        border-radius: 50px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 10px 30px rgba(233, 69, 96, 0.3);
      }
      .start-btn:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(233, 69, 96, 0.4);
      }
      .start-btn:active {
        transform: translateY(0);
      }
      @media (max-width: 600px) {
        .title-text { font-size: 2.5rem; }
        .title-chicken { font-size: 5rem; }
      }
    </style>
    <div class="title-screen">
      <div class="title-chicken">🐔</div>
      <h1 class="title-text">Chicken Empire</h1>
      <p class="title-subtitle">GCC Poultry Tycoon</p>
      <div class="title-fact">
        💡 ${FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)].text}
      </div>
      <button class="start-btn" id="start-btn">Start Empire</button>
    </div>
  `;

  document.getElementById('start-btn')?.addEventListener('click', () => {
    game.state.phase = 'country-select';
    renderCountrySelect();
  });
}

function renderCountrySelect() {
  const unlockedCountries = game.state.unlockedCountries;

  app.innerHTML = `
    <style>
      .country-select {
        min-height: 100vh;
        padding: 2rem;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
      }
      .select-header {
        text-align: center;
        margin-bottom: 2rem;
      }
      .select-title {
        font-family: 'Lilita One', cursive;
        font-size: 2.5rem;
        color: var(--gold);
        margin-bottom: 0.5rem;
      }
      .select-subtitle {
        color: #888;
        font-size: 1.1rem;
      }
      .countries-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
        max-width: 1200px;
        margin: 0 auto;
      }
      .country-card {
        background: var(--bg-card);
        border-radius: 16px;
        padding: 1.5rem;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 2px solid transparent;
        position: relative;
        overflow: hidden;
      }
      .country-card:hover {
        transform: translateY(-5px);
        border-color: var(--accent);
        box-shadow: 0 10px 30px rgba(233, 69, 96, 0.2);
      }
      .country-card.locked {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .country-card.locked:hover {
        transform: none;
        border-color: transparent;
      }
      .country-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1rem;
      }
      .country-flag {
        font-size: 3rem;
      }
      .country-name {
        font-family: 'Lilita One', cursive;
        font-size: 1.5rem;
        color: white;
      }
      .country-difficulty {
        font-size: 0.75rem;
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        background: var(--accent);
        color: white;
        margin-left: auto;
      }
      .country-difficulty.easy { background: var(--green); }
      .country-difficulty.medium { background: #f39c12; }
      .country-difficulty.hard { background: var(--accent); }
      .country-difficulty.expert { background: #9b59b6; }
      .country-desc {
        color: #aaa;
        font-size: 0.9rem;
        margin-bottom: 1rem;
        line-height: 1.5;
      }
      .country-stats {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }
      .country-stat {
        font-size: 0.85rem;
        color: #888;
      }
      .country-stat span {
        color: var(--gold);
        font-weight: 600;
      }
      .country-fact {
        background: rgba(255,201,71,0.1);
        padding: 0.75rem;
        border-radius: 8px;
        font-size: 0.85rem;
        color: var(--gold);
        border-left: 3px solid var(--gold);
      }
      .locked-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2rem;
        color: #888;
      }
      .prestige-info {
        text-align: center;
        margin-top: 2rem;
        padding: 1rem;
        background: rgba(255,255,255,0.05);
        border-radius: 12px;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      }
      .prestige-info h3 {
        color: var(--gold);
        margin-bottom: 0.5rem;
      }
    </style>
    <div class="country-select">
      <div class="select-header">
        <h1 class="select-title">Choose Your Market</h1>
        <p class="select-subtitle">Each country has unique challenges and opportunities</p>
      </div>
      <div class="countries-grid">
        ${Object.values(COUNTRIES).map(country => {
          const isUnlocked = unlockedCountries.includes(country.code);
          return `
            <div class="country-card ${isUnlocked ? '' : 'locked'}" data-country="${country.code}">
              <div class="country-header">
                <span class="country-flag">${country.flag}</span>
                <span class="country-name">${country.name}</span>
                <span class="country-difficulty ${country.difficulty.toLowerCase()}">${country.difficulty}</span>
              </div>
              <p class="country-desc">${country.description}</p>
              <div class="country-stats">
                <div class="country-stat">Self-Sufficiency: <span>${country.selfSufficiency}%</span></div>
                <div class="country-stat">Target: <span>${country.targetSelfSufficiency}%</span></div>
                <div class="country-stat">Consumption: <span>${country.perCapitaConsumption}kg/yr</span></div>
                <div class="country-stat">Feed Subsidy: <span>${country.feedSubsidy}%</span></div>
              </div>
              <div class="country-fact">${country.funFact}</div>
              ${!isUnlocked ? `<div class="locked-overlay">🔒 Prestige to Unlock</div>` : ''}
            </div>
          `;
        }).join('')}
      </div>
      ${game.state.prestigeLevel > 0 ? `
        <div class="prestige-info">
          <h3>🏆 Prestige Level: ${game.state.prestigeLevel}</h3>
          <p>Export Licenses: ${game.state.exportLicenses} (+${game.state.prestigeLevel * 10}% production bonus)</p>
        </div>
      ` : ''}
    </div>
  `;

  document.querySelectorAll('.country-card:not(.locked)').forEach(card => {
    card.addEventListener('click', () => {
      const country = card.getAttribute('data-country') as CountryCode;
      game.startGame(country);
      renderGame(game.state);
    });
  });
}

function renderGame(state: GameState) {
  const country = COUNTRIES[state.country];
  const price = game.calculatePrice();

  app.innerHTML = `
    <style>
      .game-container {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      /* Header */
      .game-header {
        background: var(--bg-secondary);
        padding: 1rem 1.5rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid rgba(255,255,255,0.1);
        flex-wrap: wrap;
        gap: 1rem;
      }
      .header-left {
        display: flex;
        align-items: center;
        gap: 1rem;
      }
      .country-badge {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-family: 'Lilita One', cursive;
        font-size: 1.2rem;
      }
      .header-stats {
        display: flex;
        gap: 1.5rem;
        flex-wrap: wrap;
      }
      .stat-box {
        text-align: center;
      }
      .stat-value {
        font-family: 'Lilita One', cursive;
        font-size: 1.3rem;
        color: var(--gold);
      }
      .stat-label {
        font-size: 0.75rem;
        color: #888;
        text-transform: uppercase;
      }

      /* Main content */
      .game-main {
        flex: 1;
        display: grid;
        grid-template-columns: 1fr 350px;
        gap: 0;
      }
      @media (max-width: 900px) {
        .game-main {
          grid-template-columns: 1fr;
        }
      }

      /* Chicken area */
      .chicken-area {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
        position: relative;
        min-height: 400px;
      }
      .chicken-button {
        font-size: 10rem;
        background: none;
        border: none;
        cursor: pointer;
        transition: transform 0.1s ease;
        filter: drop-shadow(0 10px 30px rgba(255,201,71,0.3));
        position: relative;
        z-index: 10;
      }
      .chicken-button:hover {
        transform: scale(1.05);
      }
      .chicken-button:active {
        transform: scale(0.95);
      }
      .chicken-count {
        font-family: 'Lilita One', cursive;
        font-size: 3rem;
        color: white;
        margin-top: 1rem;
      }
      .chicken-per-sec {
        color: #888;
        font-size: 1rem;
      }
      .sell-btn {
        margin-top: 1.5rem;
        padding: 0.75rem 2rem;
        font-family: 'Lilita One', cursive;
        font-size: 1.1rem;
        background: linear-gradient(135deg, var(--green) 0%, #00a078 100%);
        color: white;
        border: none;
        border-radius: 30px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 5px 20px rgba(0,200,151,0.3);
      }
      .sell-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0,200,151,0.4);
      }
      .sell-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }

      /* Self-sufficiency meter */
      .sufficiency-meter {
        position: absolute;
        bottom: 2rem;
        left: 2rem;
        right: 2rem;
        background: rgba(0,0,0,0.3);
        border-radius: 12px;
        padding: 1rem;
      }
      .sufficiency-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
      }
      .sufficiency-bar {
        height: 20px;
        background: rgba(255,255,255,0.1);
        border-radius: 10px;
        overflow: hidden;
        position: relative;
      }
      .sufficiency-fill {
        height: 100%;
        background: linear-gradient(90deg, var(--accent) 0%, var(--gold) 100%);
        border-radius: 10px;
        transition: width 0.5s ease;
      }
      .sufficiency-target {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 3px;
        background: white;
      }

      /* Shop panel */
      .shop-panel {
        background: var(--bg-card);
        padding: 1.5rem;
        overflow-y: auto;
        max-height: calc(100vh - 80px);
      }
      .shop-title {
        font-family: 'Lilita One', cursive;
        font-size: 1.5rem;
        color: var(--gold);
        margin-bottom: 1rem;
        text-align: center;
      }
      .shop-tabs {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }
      .shop-tab {
        flex: 1;
        padding: 0.5rem;
        background: rgba(255,255,255,0.05);
        border: none;
        border-radius: 8px;
        color: #888;
        cursor: pointer;
        font-size: 0.85rem;
        transition: all 0.2s ease;
      }
      .shop-tab.active {
        background: var(--accent);
        color: white;
      }
      .shop-item {
        background: rgba(0,0,0,0.2);
        border-radius: 12px;
        padding: 1rem;
        margin-bottom: 0.75rem;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 2px solid transparent;
      }
      .shop-item:hover:not(.locked):not(.maxed) {
        border-color: var(--accent);
        background: rgba(233,69,96,0.1);
      }
      .shop-item.locked {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .shop-item.maxed {
        opacity: 0.6;
        cursor: not-allowed;
        border-color: var(--green);
      }
      .shop-item.affordable {
        border-color: var(--green);
      }
      .item-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.5rem;
      }
      .item-icon {
        font-size: 1.5rem;
      }
      .item-name {
        font-weight: 600;
        color: white;
        flex: 1;
      }
      .item-count {
        background: var(--bg-secondary);
        padding: 0.25rem 0.5rem;
        border-radius: 6px;
        font-size: 0.85rem;
        color: var(--gold);
      }
      .item-desc {
        font-size: 0.8rem;
        color: #888;
        margin-bottom: 0.5rem;
      }
      .item-cost {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.85rem;
      }
      .cost-amount {
        color: var(--gold);
        font-weight: 600;
      }
      .item-production {
        color: var(--green);
      }

      /* Events */
      .active-events {
        position: fixed;
        top: 100px;
        right: 20px;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        z-index: 100;
      }
      .event-badge {
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        animation: slideIn 0.3s ease;
      }
      .event-badge.positive {
        background: linear-gradient(135deg, var(--green), #00a078);
        color: white;
      }
      .event-badge.negative {
        background: linear-gradient(135deg, var(--accent), #c73659);
        color: white;
      }
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }

      /* Toast */
      .toast {
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%) translateY(100px);
        padding: 1rem 2rem;
        border-radius: 12px;
        background: var(--bg-card);
        color: white;
        font-size: 0.95rem;
        box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        transition: transform 0.3s ease;
        z-index: 1000;
        max-width: 90%;
        text-align: center;
      }
      .toast.show {
        transform: translateX(-50%) translateY(0);
      }
      .toast-success {
        border-left: 4px solid var(--green);
      }
      .toast-info {
        border-left: 4px solid var(--gold);
      }
      .toast-warning {
        border-left: 4px solid var(--accent);
      }

      /* Click effect */
      .click-effect {
        position: absolute;
        pointer-events: none;
        font-size: 1.5rem;
        font-weight: bold;
        color: var(--gold);
        animation: float-up 1s ease-out forwards;
        z-index: 100;
      }
      @keyframes float-up {
        0% { opacity: 1; transform: translateY(0) scale(1); }
        100% { opacity: 0; transform: translateY(-80px) scale(1.5); }
      }

      /* Menu button */
      .menu-btn {
        padding: 0.5rem 1rem;
        background: rgba(255,255,255,0.1);
        border: none;
        border-radius: 8px;
        color: #888;
        cursor: pointer;
        font-size: 0.85rem;
        transition: all 0.2s ease;
      }
      .menu-btn:hover {
        background: rgba(255,255,255,0.2);
        color: white;
      }
    </style>

    <div class="game-container">
      <header class="game-header">
        <div class="header-left">
          <div class="country-badge">
            ${country.flag} ${country.name}
          </div>
        </div>
        <div class="header-stats">
          <div class="stat-box">
            <div class="stat-value">${country.currencySymbol} ${game.formatNumber(state.money)}</div>
            <div class="stat-label">Money</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${game.formatNumber(state.chickensPerSecond)}/s</div>
            <div class="stat-label">Production</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${game.formatNumber(price)}</div>
            <div class="stat-label">Price</div>
          </div>
          <div class="stat-box">
            <div class="stat-value">${formatTime(state.playTime)}</div>
            <div class="stat-label">Time</div>
          </div>
        </div>
        <button class="menu-btn" id="menu-btn">☰ Menu</button>
      </header>

      <main class="game-main">
        <div class="chicken-area" id="chicken-area">
          <button class="chicken-button" id="chicken-btn">🐔</button>
          <div class="chicken-count">${game.formatNumber(state.chickens)} Chickens</div>
          <div class="chicken-per-sec">${game.formatNumber(state.chickensPerSecond)} per second</div>
          <button class="sell-btn" id="sell-btn" ${state.chickens < 1 ? 'disabled' : ''}>
            Sell All (${country.currencySymbol} ${game.formatNumber(state.chickens * price)})
          </button>

          <div class="sufficiency-meter">
            <div class="sufficiency-header">
              <span>Self-Sufficiency: ${state.selfSufficiency.toFixed(1)}%</span>
              <span>Target: ${country.targetSelfSufficiency}%</span>
            </div>
            <div class="sufficiency-bar">
              <div class="sufficiency-fill" style="width: ${state.selfSufficiency}%"></div>
              <div class="sufficiency-target" style="left: ${country.targetSelfSufficiency}%"></div>
            </div>
          </div>
        </div>

        <div class="shop-panel">
          <h2 class="shop-title">🏪 Empire Builder</h2>
          <div class="shop-tabs">
            <button class="shop-tab active" data-tab="buildings">Buildings</button>
            <button class="shop-tab" data-tab="upgrades">Upgrades</button>
            <button class="shop-tab" data-tab="achievements">🏆</button>
          </div>
          <div id="shop-content">
            ${renderBuildings(state)}
          </div>
        </div>
      </main>

      <div class="active-events" id="active-events">
        ${state.activeEvents.map(ae => `
          <div class="event-badge ${ae.event.isPositive ? 'positive' : 'negative'}">
            ${ae.event.icon} ${ae.event.title}
          </div>
        `).join('')}
      </div>
    </div>
  `;

  setupGameEventListeners(state);
}

function renderBuildings(state: GameState): string {
  return Object.values(state.buildings)
    .map(building => {
      const cost = game.calculateBuildingCost(building.id);
      const isLocked = building.unlockAt !== undefined && state.totalChickensProduced < building.unlockAt;
      const isAffordable = state.money >= cost;

      return `
        <div class="shop-item ${isLocked ? 'locked' : ''} ${isAffordable && !isLocked ? 'affordable' : ''}"
             data-building="${building.id}">
          <div class="item-header">
            <span class="item-icon">${building.icon}</span>
            <span class="item-name">${building.name}</span>
            <span class="item-count">x${building.count}</span>
          </div>
          <div class="item-desc">${building.description}</div>
          <div class="item-cost">
            <span class="cost-amount">${COUNTRIES[state.country].currencySymbol} ${game.formatNumber(cost)}</span>
            <span class="item-production">+${game.formatNumber(building.baseProduction)}/s</span>
          </div>
          ${isLocked ? `<div style="font-size: 0.75rem; color: #888; margin-top: 0.5rem;">🔒 Unlock at ${game.formatNumber(building.unlockAt!)} chickens</div>` : ''}
        </div>
      `;
    }).join('');
}

function renderUpgrades(state: GameState): string {
  return Object.values(state.upgrades)
    .map(upgrade => {
      const cost = game.calculateUpgradeCost(upgrade.id);
      const isLocked = upgrade.unlockAt !== undefined && state.totalChickensProduced < upgrade.unlockAt;
      const isMaxed = upgrade.level >= upgrade.maxLevel;
      const isAffordable = state.money >= cost;

      return `
        <div class="shop-item ${isLocked ? 'locked' : ''} ${isMaxed ? 'maxed' : ''} ${isAffordable && !isLocked && !isMaxed ? 'affordable' : ''}"
             data-upgrade="${upgrade.id}">
          <div class="item-header">
            <span class="item-icon">${upgrade.icon}</span>
            <span class="item-name">${upgrade.name}</span>
            <span class="item-count">${isMaxed ? 'MAX' : `${upgrade.level}/${upgrade.maxLevel}`}</span>
          </div>
          <div class="item-desc">${upgrade.description}</div>
          <div class="item-cost">
            <span class="cost-amount">${isMaxed ? '✓ Complete' : `${COUNTRIES[state.country].currencySymbol} ${game.formatNumber(cost)}`}</span>
          </div>
          ${isLocked ? `<div style="font-size: 0.75rem; color: #888; margin-top: 0.5rem;">🔒 Unlock at ${game.formatNumber(upgrade.unlockAt!)} chickens</div>` : ''}
        </div>
      `;
    }).join('');
}

function renderAchievements(state: GameState): string {
  return Object.values(state.achievements)
    .map(achievement => {
      let progress = 0;
      switch (achievement.type) {
        case 'chickens':
          progress = Math.min(100, (state.totalChickensProduced / achievement.requirement) * 100);
          break;
        case 'money':
          progress = Math.min(100, (state.totalMoneyEarned / achievement.requirement) * 100);
          break;
        case 'selfSufficiency':
          progress = Math.min(100, (state.selfSufficiency / achievement.requirement) * 100);
          break;
        case 'buildings':
          const total = Object.values(state.buildings).reduce((sum, b) => sum + b.count, 0);
          progress = Math.min(100, (total / achievement.requirement) * 100);
          break;
      }

      return `
        <div class="shop-item ${achievement.unlocked ? 'maxed' : ''}">
          <div class="item-header">
            <span class="item-icon">${achievement.icon}</span>
            <span class="item-name">${achievement.title}</span>
            <span class="item-count">${achievement.unlocked ? '✓' : `${progress.toFixed(0)}%`}</span>
          </div>
          <div class="item-desc">${achievement.description}</div>
          ${achievement.reward ? `<div style="font-size: 0.75rem; color: var(--gold); margin-top: 0.5rem;">🎁 Reward: ${achievement.reward}</div>` : ''}
        </div>
      `;
    }).join('');
}

function setupGameEventListeners(state: GameState) {
  // Chicken click
  const chickenBtn = document.getElementById('chicken-btn');
  const chickenArea = document.getElementById('chicken-area');

  chickenBtn?.addEventListener('click', (e) => {
    game.clickChicken();

    // Click effect
    const effect = document.createElement('div');
    effect.className = 'click-effect';
    effect.textContent = '+' + (1 + Math.floor(state.totalChickensProduced / 1000));
    effect.style.left = (e as MouseEvent).clientX + 'px';
    effect.style.top = (e as MouseEvent).clientY + 'px';
    chickenArea?.appendChild(effect);
    setTimeout(() => effect.remove(), 1000);
  });

  // Sell button
  document.getElementById('sell-btn')?.addEventListener('click', () => {
    game.sellChickens();
  });

  // Shop tabs
  document.querySelectorAll('.shop-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.shop-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      const tabType = tab.getAttribute('data-tab');
      const content = document.getElementById('shop-content');
      if (content) {
        if (tabType === 'buildings') {
          content.innerHTML = renderBuildings(state);
          setupBuildingListeners();
        } else if (tabType === 'upgrades') {
          content.innerHTML = renderUpgrades(state);
          setupUpgradeListeners();
        } else if (tabType === 'achievements') {
          content.innerHTML = renderAchievements(state);
        }
      }
    });
  });

  setupBuildingListeners();

  // Menu button
  document.getElementById('menu-btn')?.addEventListener('click', () => {
    if (confirm('Reset game? (This will erase all progress)')) {
      game.resetGame();
      renderTitleScreen();
    }
  });
}

function setupBuildingListeners() {
  document.querySelectorAll('[data-building]').forEach(item => {
    item.addEventListener('click', () => {
      const buildingId = item.getAttribute('data-building')!;
      if (game.buyBuilding(buildingId)) {
        showToast(`🏗️ Built ${game.state.buildings[buildingId].name}!`, 'success');
      }
    });
  });
}

function setupUpgradeListeners() {
  document.querySelectorAll('[data-upgrade]').forEach(item => {
    item.addEventListener('click', () => {
      const upgradeId = item.getAttribute('data-upgrade')!;
      if (game.buyUpgrade(upgradeId)) {
        showToast(`⬆️ Upgraded ${game.state.upgrades[upgradeId].name}!`, 'success');
      }
    });
  });
}

// Initialize game
game = new ChickenEmpireGame(
  // onUpdate
  (state) => {
    if (state.phase === 'playing') {
      // Update dynamic elements without full re-render
      const chickenCount = document.querySelector('.chicken-count');
      const chickenPerSec = document.querySelector('.chicken-per-sec');
      const sellBtn = document.getElementById('sell-btn') as HTMLButtonElement;
      const moneyDisplay = document.querySelector('.stat-box:first-child .stat-value');
      const productionDisplay = document.querySelector('.stat-box:nth-child(2) .stat-value');
      const sufficiencyFill = document.querySelector('.sufficiency-fill') as HTMLElement;
      const sufficiencyText = document.querySelector('.sufficiency-header span:first-child');

      const price = game.calculatePrice();
      const country = COUNTRIES[state.country];

      if (chickenCount) chickenCount.textContent = `${game.formatNumber(state.chickens)} Chickens`;
      if (chickenPerSec) chickenPerSec.textContent = `${game.formatNumber(state.chickensPerSecond)} per second`;
      if (sellBtn) {
        sellBtn.disabled = state.chickens < 1;
        sellBtn.textContent = `Sell All (${country.currencySymbol} ${game.formatNumber(state.chickens * price)})`;
      }
      if (moneyDisplay) moneyDisplay.textContent = `${country.currencySymbol} ${game.formatNumber(state.money)}`;
      if (productionDisplay) productionDisplay.textContent = `${game.formatNumber(state.chickensPerSecond)}/s`;
      if (sufficiencyFill) sufficiencyFill.style.width = `${state.selfSufficiency}%`;
      if (sufficiencyText) sufficiencyText.textContent = `Self-Sufficiency: ${state.selfSufficiency.toFixed(1)}%`;

      // Update shop if visible
      const activeTab = document.querySelector('.shop-tab.active');
      const tabType = activeTab?.getAttribute('data-tab');
      const content = document.getElementById('shop-content');
      if (content && tabType === 'buildings') {
        content.innerHTML = renderBuildings(state);
        setupBuildingListeners();
      } else if (content && tabType === 'upgrades') {
        content.innerHTML = renderUpgrades(state);
        setupUpgradeListeners();
      } else if (content && tabType === 'achievements') {
        content.innerHTML = renderAchievements(state);
      }

      // Update events display
      const eventsContainer = document.getElementById('active-events');
      if (eventsContainer) {
        eventsContainer.innerHTML = state.activeEvents.map(ae => `
          <div class="event-badge ${ae.event.isPositive ? 'positive' : 'negative'}">
            ${ae.event.icon} ${ae.event.title}
          </div>
        `).join('');
      }
    } else if (state.phase === 'prestige') {
      renderPrestige(state);
    }
  },
  // onAchievement
  (achievementId) => {
    const achievement = game.state.achievements[achievementId];
    showToast(`🏆 Achievement Unlocked: ${achievement.title}!`, 'success');
  },
  // onEvent
  (event) => {
    showToast(`${event.icon} ${event.title}: ${event.description}`, event.isPositive ? 'success' : 'warning');
  },
  // onFact
  (fact) => {
    showToast(`💡 Did you know? ${fact}`, 'info');
  }
);

function renderPrestige(state: GameState) {
  const licenses = Math.floor(Math.log10(state.totalChickensProduced + 1));

  app.innerHTML = `
    <style>
      .prestige-screen {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        text-align: center;
      }
      .prestige-trophy {
        font-size: 8rem;
        animation: pulse 2s ease-in-out infinite;
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
      .prestige-title {
        font-family: 'Lilita One', cursive;
        font-size: 3rem;
        background: linear-gradient(135deg, #ffc947 0%, #e94560 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        margin: 1rem 0;
      }
      .prestige-stats {
        background: rgba(0,0,0,0.3);
        padding: 1.5rem 2rem;
        border-radius: 16px;
        margin: 1rem 0 2rem;
      }
      .prestige-stat {
        margin: 0.5rem 0;
        font-size: 1.1rem;
        color: #aaa;
      }
      .prestige-stat span {
        color: var(--gold);
        font-weight: 600;
      }
      .prestige-btn {
        font-family: 'Lilita One', cursive;
        font-size: 1.5rem;
        padding: 1rem 3rem;
        background: linear-gradient(135deg, #ffc947 0%, #e94560 100%);
        color: white;
        border: none;
        border-radius: 50px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 10px 30px rgba(255,201,71,0.3);
      }
      .prestige-btn:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 40px rgba(255,201,71,0.4);
      }
    </style>
    <div class="prestige-screen">
      <div class="prestige-trophy">🏆</div>
      <h1 class="prestige-title">Food Security Achieved!</h1>
      <p style="color: #888; font-size: 1.2rem; max-width: 500px;">
        You've achieved 100% self-sufficiency! Your chicken empire has secured the nation's food supply.
      </p>
      <div class="prestige-stats">
        <div class="prestige-stat">Total Chickens Produced: <span>${game.formatNumber(state.totalChickensProduced)}</span></div>
        <div class="prestige-stat">Total Revenue: <span>${game.formatNumber(state.totalMoneyEarned)}</span></div>
        <div class="prestige-stat">Play Time: <span>${formatTime(state.playTime)}</span></div>
        <div class="prestige-stat">Export Licenses Earned: <span>+${licenses}</span></div>
      </div>
      <button class="prestige-btn" id="prestige-btn">
        🚀 Expand to New Market (+${licenses} Export Licenses)
      </button>
    </div>
  `;

  document.getElementById('prestige-btn')?.addEventListener('click', () => {
    game.prestige();
    renderCountrySelect();
  });
}

// Start the game
if (game.state.phase === 'title') {
  renderTitleScreen();
} else if (game.state.phase === 'country-select') {
  renderCountrySelect();
} else if (game.state.phase === 'playing') {
  game.startTicking();
  renderGame(game.state);
} else if (game.state.phase === 'prestige') {
  renderPrestige(game.state);
}
